import { Logger, withPrefix } from '@gitlab-org/logging';
import { AIContextItem, SystemContextManager, type WorkflowContext } from '@gitlab-org/ai-context';
import { workspaceFolderPathFromUri } from '@gitlab-org/fs';
import {
  DuoWorkflowStatus,
  generateErrorMessageFromStatusCode,
  isDuoWorkflowEvent,
  isTransientStatusCode,
  isWorkflowExecutorErrorEvent,
  isWorkflowRetryEvent,
  RunWorkflowPayload,
  type ToolApproval,
  ToolApprovalType,
  WorkflowRunner,
  WorkflowStatusCode,
  WorkflowDefinition,
} from '@gitlab-lsp/workflow-api';
import { isUsageQuotaExceededError } from '@gitlab-lsp/workflow-api/node';
import {
  type WorkflowId,
  isAgenticChatForbiddenError,
  isDuoCliDisabledError,
  isNoDuoNamespaceError,
} from '@gitlab-org/workflow-executor';
import { isSandboxUnavailableError } from '@gitlab-org/sandbox/errors';
import { ConfigService } from '@gitlab-org/config';
import { ProjectDetails, ProjectService, tryParseGitLabGidToString } from '@gitlab-org/core';
import { McpManagerWorkflowExecutorAdaptor } from '@gitlab-org/ai-configuration';
import { DuoAgentPlatformEvent, DuoAgentPlatformTracker } from '@gitlab-org/telemetry';
import { supportsToolCallApprovals } from '@gitlab-org/tool-approval';
import {
  type AgentEvent,
  AgentEventType,
  type AgentMode,
  type ApprovalScope,
  type BackendInitResult,
  CliBackend,
  type UserAction,
  UserActionType,
} from '../backend';
import { getAgentModeConfig } from '../../agents/agents';
import type { ParsedCliInput } from '../../parse';
import type { SessionDataService } from '../../sessions/session_data_service';
import type { PresentationPolicy } from '../presentation_policy';
import type { GitLabModelManager } from './gitlab_model_manager';
import type { GitLabParsedOptions } from './gitlab_parsed_options';
import type { RootNamespaceIdService } from './root_namespace_id_service';
import { buildChatPartialFlowConfig, FLOW_CONFIG_SCHEMA_VERSION } from './flow_config_builder';
import { WorkflowEventMapper } from './workflow_event_mapper';
import { WorkflowAgentPrivileges } from './workflow_agent_privileges';
import { buildPlanContextItem } from './system_context/plan_context';
import type { RunCondition } from './run_condition';
import { ResumeIdentityError } from './workflow_strategy';
import type {
  FlowDescriptor,
  WorkflowStrategy,
  WorkflowStrategyContext,
} from './workflow_strategy';
import type { WorkflowState } from './workflow_state';

export class GitLabBackend implements CliBackend {
  readonly id = 'gitlab';

  #logger: Logger;

  #workflowRunner: WorkflowRunner;

  #configService: ConfigService;

  #cliInput: ParsedCliInput;

  #backendOpts: GitLabParsedOptions;

  #projectService: ProjectService;

  #rootNamespaceIdService: RootNamespaceIdService;

  #workflowEventMapper: WorkflowEventMapper;

  #systemContextManager: SystemContextManager;

  #mcpManager: McpManagerWorkflowExecutorAdaptor;

  #modelManager: GitLabModelManager;

  #duoAgentPlatformTracker: DuoAgentPlatformTracker;

  #sessionDataService: SessionDataService;

  // Optional only because it is unset until #acquireWorkflow resolves it; a
  // live/resumed workflow always has it.
  #flow?: FlowDescriptor;

  // Server capabilities read from `aiFlowsMetadata`, fetched in #ensureServerDefaultFlow.
  // `undefined` means not yet fetched; `null` means the instance doesn't advertise `aiFlowsMetadata`
  // (<19.2) or the query failed, so callers fall back to the token-derived
  // WorkflowRunner#getServerCapabilities. A resolved (possibly empty) array is
  // preferred over the token-derived one, since it's available without
  // needing a workflow token (i.e. no `direct_access` request).
  #serverCapabilitiesFromMetadata?: string[] | null;

  #workflow: WorkflowState = { type: 'none' };

  #projectDetails?: ProjectDetails;

  #systemContext: AIContextItem[] = [];

  #firstMessageContext: AIContextItem[] | null = null;

  #activeAgentMode: AgentMode | undefined;

  #privileges: WorkflowAgentPrivileges;

  #workflowCapabilities = new Map<string, string[]>();

  #runCondition: RunCondition;

  #strategy: WorkflowStrategy;

  #policy: PresentationPolicy;

  #autoApprove: boolean;

  constructor(
    logger: Logger,
    workflowRunner: WorkflowRunner,
    configService: ConfigService,
    cliInput: ParsedCliInput,
    backendOpts: GitLabParsedOptions,
    projectService: ProjectService,
    rootNamespaceIdService: RootNamespaceIdService,
    workflowEventMapper: WorkflowEventMapper,
    systemContextManager: SystemContextManager,
    mcpManager: McpManagerWorkflowExecutorAdaptor,
    modelManager: GitLabModelManager,
    duoAgentPlatformTracker: DuoAgentPlatformTracker,
    sessionDataService: SessionDataService,
    runCondition: RunCondition,
    strategy: WorkflowStrategy,
    policy: PresentationPolicy,
    autoApprove: boolean,
  ) {
    this.#logger = withPrefix(logger, '[GitLabBackend]');
    this.#workflowRunner = workflowRunner;
    this.#configService = configService;
    this.#cliInput = cliInput;
    this.#backendOpts = backendOpts;
    this.#projectService = projectService;
    this.#rootNamespaceIdService = rootNamespaceIdService;
    this.#workflowEventMapper = workflowEventMapper;
    this.#systemContextManager = systemContextManager;
    this.#mcpManager = mcpManager;
    this.#modelManager = modelManager;
    this.#duoAgentPlatformTracker = duoAgentPlatformTracker;
    this.#sessionDataService = sessionDataService;
    this.#runCondition = runCondition;
    this.#strategy = strategy;
    this.#policy = policy;
    this.#autoApprove = autoApprove;
    this.#privileges = new WorkflowAgentPrivileges(workflowRunner, this.#autoApprove);
  }

  get #workflowId(): WorkflowId | undefined {
    return 'id' in this.#workflow ? this.#workflow.id : undefined;
  }

  async preinitialize(): Promise<void> {
    this.#mcpManager.preWarm(this.#cliInput.cwd);
  }

  getSessionId(): WorkflowId | undefined {
    return this.#workflowId;
  }

  #errorEvent(message: string): AgentEvent {
    return { type: AgentEventType.Error, message, timestamp: Date.now() };
  }

  dispose(): void {
    this.#workflow = { type: 'none' };
    this.#firstMessageContext = null;
    this.#activeAgentMode = undefined;
    this.#workflowCapabilities.clear();
    this.#logger.debug('GitLabBackend disposed');
  }

  /**
   * Determines which approval scopes are available for tool approvals.
   *
   * @returns Array of available approval scopes, defaulting to ['once'] if no capabilities
   */
  #getAvailableApprovalScopes(): ApprovalScope[] {
    const workflowId = this.#workflowId;

    // If no workflow yet, return minimal set
    if (!workflowId) {
      return ['once'];
    }

    const capabilities = this.#workflowCapabilities.get(workflowId) ?? null;

    const supportsSession = supportsToolCallApprovals({
      logger: this.#logger,
      capabilities,
    });

    return supportsSession ? ['once', 'session'] : ['once'];
  }

  /**
   * Backend initialisation specific to GitLab backend.
   * Common initialisation already run at this point via CliInitialisationService
   */
  async initialize(): Promise<BackendInitResult> {
    if (this.#backendOpts.gitlabProjectPath) {
      this.#configService.set('projectPath', this.#backendOpts.gitlabProjectPath);
    }

    const projectPath =
      this.#backendOpts.gitlabProjectPath || this.#configService.get('projectPath');

    if (projectPath) {
      this.#logger.debug(`Fetching project details for: ${projectPath}`);
      try {
        this.#projectDetails =
          await this.#projectService.getProjectFromPathWithNamespace(projectPath);
        this.#logger.debug(`Fetched project details for ${projectPath}`);
      } catch (error) {
        this.#logger.warn(
          `Failed to fetch project details for ${projectPath}. Some features may be unavailable.`,
          error,
        );
      }
    } else {
      this.#logger.debug(
        "No project path found in config. Falling back to user's configured default Duo namespace.",
      );
    }

    const rootNamespaceId = await this.#rootNamespaceIdService.resolve(this.#projectDetails);
    this.#configService.set('rootNamespaceId', rootNamespaceId);
    await this.#modelManager.initialize();
    await this.#modelManager.resolveModel();
    await this.#ensureServerDefaultFlow();

    // Local resume only: attaching to the CLI's own prior session. rails always
    // hands a fresh workflow (no resume), so this is undefined there.
    const resumeWorkflowId =
      this.#runCondition.source === 'local' ? this.#runCondition.resumeWorkflowId : undefined;

    let sessionRejectionReason: string | undefined;

    // The single acquire chokepoint: attach (rails / local resume) or create
    // (local new). sendMessageStream() never creates, so this is the only
    // create/attach site.
    try {
      await this.#acquireWorkflow();
    } catch (error) {
      sessionRejectionReason = this.#createAccessDeniedResult(error);
    }

    // #workflowId is only set once acquire succeeded. Without it, a refused
    // resume would still fetch history and reconcile privileges for a session
    // we just declared unreadable, logging failures that contradict the refusal.
    if (resumeWorkflowId && this.#workflowId) {
      await this.#prepareForResume(resumeWorkflowId);

      // Reconcile privileges for local resume only, since a prior client may
      // have set them differently. rails owns the CI workflow's privileges (set
      // at creation by CreateAndStartWorkflowService from the flow definition),
      // so reconciling there would clobber them; a fresh local workflow is
      // already created with the right privileges.
      const result = await this.#privileges.reconcile(resumeWorkflowId, this.#activeAgentMode);
      if (result.type === 'error') {
        this.#logger.error(
          `Failed to set agent privileges on resume. This might mean the session doesn't have the right tools available. Underlying error: ${result.message}`,
        );
      } else if (result.type === 'applied') {
        this.#logger.debug(
          `Reconciled agent privileges on resume to '${this.#activeAgentMode ?? 'unknown'}' mode`,
        );
      }
    }

    // Fetch system context after workflow creation so hook providers have access to the session ID
    const workflowContext: WorkflowContext | undefined = this.#workflowId
      ? {
          sessionId: this.#workflowId,
          cwd: this.#resolveProjectRoot(),
          source: resumeWorkflowId ? 'resume' : 'startup',
        }
      : undefined;

    try {
      this.#systemContext = await this.#systemContextManager.getSystemContextItems(workflowContext);
      this.#logger.debug(
        `Retrieved ${this.#systemContext.length} system context items for GitLab backend`,
      );
    } catch (error) {
      this.#logger.error('Failed to get system context', error);
      this.#systemContext = [];
    }

    // For new workflows, include system context with the first message
    if (this.#workflowId && !resumeWorkflowId) {
      this.#firstMessageContext = [...this.#systemContext];
    }

    // No workflow means Agentic Chat is unavailable. Surface it as a rejection
    // reason (preserving any reason a thrown acquire already captured); the
    // controller decides whether to fail fast (headless) or display it (TUI).
    if (!this.#workflowId) {
      sessionRejectionReason ??= 'Workflow ID not available after initialization';
      this.#logger.debug('No workflow ID created - Agentic Chat is not available');
    }

    return { sessionId: this.#workflowId || '', sessionRejectionReason };
  }

  /**
   * Seeds the live event mapper with the resumed session's history so the first
   * post-resume checkpoint, which carries the full history, only emits new
   * messages. Best-effort: on failure the mapper stays unseeded (the session
   * layer's dedupe-by-id still prevents duplicate elements).
   */
  async #prepareForResume(sessionId: string): Promise<void> {
    try {
      const chatLog = await this.#sessionDataService.getSessionChatLog(sessionId);
      this.#workflowEventMapper.advanceToResumePoint(chatLog);
    } catch (error) {
      this.#logger.warn('Failed to seed event mapper from resumed session history', error);
    }
  }

  // Fetches only the capabilities half of `aiFlowsMetadata`. The default-flow
  // half is resolved once by the strategy in #acquireWorkflow (#getServerDefault),
  // scoped to the same (rootNamespaceId, projectId), so it shares this call's
  // cache entry in the runner rather than issuing a second request.
  async #ensureServerDefaultFlow(): Promise<void> {
    const rootNamespaceId = this.#configService.get('rootNamespaceId') || undefined;
    const projectId = this.#projectDetails?.id;

    this.#serverCapabilitiesFromMetadata =
      await this.#workflowRunner.getServerCapabilitiesFromMetadata(rootNamespaceId, projectId);
  }

  #detectProjectId(): string {
    return tryParseGitLabGidToString(this.#projectDetails?.id);
  }

  #detectNamespaceId(): string {
    return tryParseGitLabGidToString(this.#projectDetails?.namespace.id);
  }

  #resolveProjectRoot(): string {
    const folders = this.#configService.get('workspaceFolders') || [];
    if (folders.length > 0) {
      return workspaceFolderPathFromUri(folders[0].uri);
    }
    return this.#cliInput.cwd;
  }

  #createAccessDeniedResult(error: unknown): string {
    this.#logger.error('Failed to create workflow', error);

    // The CLI-tailored, user-facing text is mapped here rather than in the
    // service layer, which only throws a typed error carrying the raw server
    // reason. How it is surfaced (fail-fast for headless vs display for the TUI)
    // is the controller's job, driven by the returned sessionRejectionReason.
    return this.#accessDeniedMessage(error);
  }

  #accessDeniedMessage(error: unknown): string {
    // Already user-facing, and not an access problem: wrapping it would tell
    // someone with a stale session id to go check their network.
    if (error instanceof ResumeIdentityError) {
      return error.message;
    }
    if (isDuoCliDisabledError(error)) {
      return (
        'GitLab Duo CLI has not been enabled by your administrator.\n' +
        'To enable it, ask your administrator to go to:\n' +
        'Admin area > GitLab Duo > Configuration.'
      );
    }

    // Typed Duo access errors already carry an accurate, user-facing message.
    if (isAgenticChatForbiddenError(error) || isNoDuoNamespaceError(error)) {
      return error.message;
    }

    if (isUsageQuotaExceededError(error)) {
      return 'No credits remain for this billing period. Contact your administrator for more credits.';
    }

    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error checking Agentic Chat access';

    return `Failed to verify Agentic Chat access: ${errorMessage}. Please check your network connection and GitLab instance availability.`;
  }

  /**
   * The single acquire chokepoint. Delegates create/attach to the
   * source-specific {@link WorkflowStrategy} and records the resulting state,
   * capabilities, and recorded flow definition.
   */
  async #acquireWorkflow(): Promise<void> {
    const ctx: WorkflowStrategyContext = {
      logger: this.#logger,
      workflowRunner: this.#workflowRunner,
      newWorkflowMetadata: {
        rootNamespaceId: this.#configService.get('rootNamespaceId') ?? '',
        selectedModelIdentifier: this.#modelManager.getModel().modelRef,
        projectId: this.#projectDetails?.id,
      },
      privileges: this.#privileges,
      // A fresh workflow always starts in the default agent mode; per-turn
      // build<->plan switches reconcile the live workflow, so it is never
      // re-created for a mode change.
      activeAgentMode: this.#activeAgentMode,
      autoApprove: this.#autoApprove,
    };

    const acquired = await this.#strategy.acquireWorkflow(ctx);
    this.#workflow = acquired.workflow;
    this.#flow = acquired.flow;

    const workflowId = this.#workflowId;
    // Prefer capabilities from aiFlowsMetadata (no direct_access dependency);
    // fall back to the token-derived capabilities the strategy resolved via
    // direct_access on instances that don't advertise aiFlowsMetadata (<19.2).
    const capabilities = this.#serverCapabilitiesFromMetadata ?? acquired.capabilities;
    if (capabilities && workflowId) {
      this.#workflowCapabilities.set(workflowId, capabilities);
    }
  }

  async *sendMessageStream(
    action: UserAction,
    signal?: AbortSignal,
  ): AsyncGenerator<AgentEvent, void, unknown> {
    // sendMessageStream() never creates; restarting is the controller's job (a
    // new session re-enters initialize() -> acquireWorkflow()). A dead/none
    // workflow can't serve a turn, so short-circuit before any flow resolution
    // or privilege mutation — otherwise a `dead` workflow would run the
    // privilege reconcile below first, and a reconcile failure would surface
    // "Failed to switch mode..." instead of the correct session-ended message.
    if (this.#workflow.type === 'dead' || this.#workflow.type === 'none') {
      this.#logger.info(
        `Workflow is '${this.#workflow.type}'; cannot serve a turn, yielding session-ended error`,
      );
      // "failed to start" is inaccurate here: the workflow may have started and
      // later ended. Interactive recovers via `/new`; headless has none.
      yield this.#errorEvent(this.#policy.sessionEndedMessage());
      return;
    }
    // A missing flow on a live workflow means acquire never ran; fail fast so the
    // invariant violation surfaces in the logs instead of degrading the turn.
    const flow = this.#flow;
    if (!flow) {
      throw new Error('No resolved flow for active workflow; #acquireWorkflow did not run');
    }

    let flowConfigSchemaVersion: string | undefined;
    let flowConfig: string | undefined;
    let excludeMcpTools: boolean | undefined;
    let flowConfigId: string | undefined;
    let flowVersion: string | undefined;
    let workflowDefinition: string = flow.definition;

    if (action.type === UserActionType.SendPrompt) {
      this.#activeAgentMode = action.agentMode;
    }

    if (flow.developer) {
      // For a developer session, the registry developer flow is the single source of
      // truth for BOTH plan and build. The two things that differ per mode are
      // reconciled on the same flow/checkpoint:
      //   - tools: gated server-side via agent privileges (#privileges.reconcile
      //     below + privilegesForMode on create), so plan is read-only.
      //   - prompt: switched via the per-turn `plan_context` item
      //     (#getAiContextItemsForMessage), which the flow resolves into
      //     `plan_enabled`.
      // Keeping a single flow definition means plan↔build switching just resumes
      // the same FlowState checkpoint instead of swapping flow topology
      // mid-session (which closes the websocket and fails the turn).
      this.#logger.info(
        `Developer flow (registry); mode=${this.#activeAgentMode ?? 'build'} via plan_context + agent privileges`,
      );
      // Registry coordinates already resolved on #flow: the server default for a
      // new session, or the resume fallback when resuming a developer session on
      // an instance that no longer advertises a default.
      flowConfigSchemaVersion = flow.developer.flow_config_schema_version;
      flowConfigId = flow.developer.flow_config_id;
      flowVersion = flow.developer.flow_version;
      flowConfig = undefined;
      workflowDefinition = WorkflowDefinition.DEVELOPER_V1;
      // Defence-in-depth: plan mode is enforced server-side via agent
      // privileges (READ_ONLY_FILES + READ_ONLY_GITLAB), which already exclude
      // write/MCP tool dispatch. We additionally set excludeMcpTools so MCP
      // tools are never offered in plan mode even if a future privilege change
      // stops covering the MCP channel.
      excludeMcpTools = flow.excludeMcpToolsInPlan && this.#activeAgentMode === 'plan';
    } else {
      // Legacy chat-flow path: plan mode mirrors client-side as an inline
      // chat-partial config that the gateway wraps into chat.Workflow. The
      // legacy chat.Workflow does not resolve context:inputs.*, so the
      // plan_context item has no effect here and is omitted (see
      // #getAiContextItemsForMessage).
      const modeConfig = getAgentModeConfig(this.#activeAgentMode);
      if (modeConfig) {
        this.#logger.info(
          `Agent mode: ${modeConfig.name} (${modeConfig.allowedTools.length} tools, MCP ${modeConfig.excludeMcp ? 'excluded' : 'included'})`,
        );
        flowConfig = buildChatPartialFlowConfig(modeConfig);
        flowConfigSchemaVersion = FLOW_CONFIG_SCHEMA_VERSION;
        excludeMcpTools = modeConfig.excludeMcp;
      } else {
        // Explicit flow config comes from `duo run` command-line flags; the TUI
        // never registers those flags, so this is a no-op there.
        const explicit = this.#resolveExplicitFlowConfig();
        flowConfigSchemaVersion = explicit.flowConfigSchemaVersion;
        flowConfigId = explicit.flowConfigId;
        flowVersion = explicit.flowVersion;
        flowConfig = explicit.flowConfig;
      }
    }

    // The gateway re-reads privileges each turn, so reconciling the running
    // workflow applies a mode switch on the next turn without recreating it
    // (preserving history).
    if (this.#workflowId) {
      const result = await this.#privileges.reconcile(this.#workflowId, this.#activeAgentMode);
      if (result.type === 'error') {
        // In a developer session the mutation is guaranteed available (the
        // instance only advertises the developer default flow when it can serve
        // it), so abort the turn. Otherwise the switch may hit older instances
        // lacking the mutation, so degrade to the flow config's restricted
        // toolset and continue.
        if (flow.developer) {
          this.#logger.error(`Failed to update agent privileges: ${result.message}`);
          yield {
            type: AgentEventType.Error,
            message: `Failed to switch mode: could not update agent privileges. ${result.message}`,
            timestamp: Date.now(),
          };
          return;
        }
        this.#logger.warn(
          `Could not update agent privileges for mode switch; continuing with prompt/toolset enforcement only: ${result.message}`,
        );
      }
    }

    const workflowId = this.#workflow.id;

    const isResumedSession = this.#workflow.type === 'resumed';

    // A resumed workflow's capabilities are null at acquire time because no
    // token has been fetched in this process yet. The token is cached during
    // getToken() inside runWorkflow, so fetch capabilities here — otherwise a
    // resumed session stays stuck at ['once'] and never gains the 'session'
    // approval scope. Prefer aiFlowsMetadata (already fetched by
    // #ensureServerDefaultFlow, no direct_access dependency) over the
    // token-derived capabilities.
    if (!this.#workflowCapabilities.has(workflowId)) {
      const capabilities =
        this.#serverCapabilitiesFromMetadata ??
        this.#workflowRunner.getServerCapabilities(workflowId, flow.definition);
      if (capabilities) {
        this.#workflowCapabilities.set(workflowId, capabilities);
        this.#logger.info(
          `Initialized workflow ${workflowId} capabilities: [${capabilities.join(', ')}]`,
        );
      }
    }

    // Set up abort handler
    const abortHandler = () => {
      if (this.#workflowRunner.isCommandRunning(workflowId)) {
        this.#logger.info(`User interrupted running command in workflow ${workflowId}`);
        this.#workflowRunner.interruptRunningCommand(workflowId);
      } else {
        this.#logger.info(`Aborting workflow ${workflowId}`);
        this.#duoAgentPlatformTracker.trackEvent(DuoAgentPlatformEvent.WorkflowStopped, {
          source: 'chat',
          workflowId,
          reason: 'stop_button_click',
        });
        this.#workflowRunner.stopWorkflow(workflowId);
      }
    };
    signal?.addEventListener('abort', abortHandler);

    try {
      // Build metadata object, using pre-configured values if available
      const metadata = {
        projectId: this.#backendOpts.duoWorkflowProjectId || this.#detectProjectId(),
        namespaceId: this.#backendOpts.duoWorkflowNamespaceId || this.#detectNamespaceId(),
        rootNamespaceId: this.#configService.get('rootNamespaceId') ?? '',
        selectedModelIdentifier: this.#modelManager.getModel().modelRef,
      };

      let toolApproval: ToolApproval | undefined;
      if (action.type === UserActionType.SendToolApproval) {
        if (!action.approved) {
          // action.approvalSource is intentionally not forwarded on rejections;
          // ToolApprovalRejected carries no approvalSource by design (see its docs in
          // workflow_message_types.ts).
          toolApproval = {
            userApproved: false as const,
            message: action.rejectionReason,
          };
        } else if (action.scope === 'once' || !action.toolArgs) {
          toolApproval = {
            userApproved: true as const,
            toolName: action.toolName,
            type: ToolApprovalType.APPROVE_ONCE,
            toolArgs: action.toolArgs,
            approvalSource: action.approvalSource,
          };
        } else if (action.pattern) {
          toolApproval = {
            userApproved: true as const,
            toolName: action.toolName,
            type: ToolApprovalType.APPROVE_PATTERN_FOR_SESSION,
            pattern: action.pattern,
            approvalSource: action.approvalSource,
          };
        } else {
          toolApproval = {
            userApproved: true as const,
            toolName: action.toolName,
            type: ToolApprovalType.APPROVE_FOR_SESSION,
            toolArgs: action.toolArgs,
            approvalSource: action.approvalSource,
          };
        }
      }

      const workflowPayload: RunWorkflowPayload = {
        goal: this.#policy.resolveGoal(action),
        metadata,
        workflowDefinition,
        existingWorkflowId: workflowId,
        additionalContext: this.#getAiContextItemsForMessage(action, flow),
        toolApproval,
        flowConfig,
        flowConfigSchemaVersion,
        flowConfigId,
        flowVersion,
        excludeMcpTools,
        // Enable token-by-token UI streaming for the interactive developer flow.
        // The developer flow opts into streaming by declaring on_agent_reasoning
        // + on_agent_final_answer in its ui_log_events.
        streaming: flow.streaming,
        supportsScheduledWakes: this.#policy.supportsScheduledWakes,
        agentPlatformFeatureSettingName: this.#backendOpts.agentPlatformFeatureSettingName,
        langsmithTrace: this.#backendOpts.langsmithTrace,
      };

      const workflowEvents = this.#workflowRunner.runWorkflow(workflowPayload);
      let noCheckpointYet = true;
      for await (const event of workflowEvents) {
        if (isWorkflowRetryEvent(event)) {
          yield {
            type: AgentEventType.Retry,
            attempt: event.attempt,
            maxAttempts: event.maxAttempts,
            backoffMs: event.backoffMs,
            timestamp: Date.now(),
          };
          // eslint-disable-next-line no-continue
          continue;
        }

        if (isWorkflowExecutorErrorEvent(event)) {
          this.#logger.error(
            `Workflow failed with status code "${event.statusCode}": ${event.message}`,
          );
          const isTransient = event.statusCode !== null && isTransientStatusCode(event.statusCode);
          if (!isTransient) {
            this.#workflow = { type: 'dead', id: workflowId };
          }
          // A transient error leaves the workflow alive, so the next message
          // reuses it — showing the "session ended" message would contradict that.
          const showSessionEnded = isResumedSession && !isTransient;
          yield this.#errorEvent(
            showSessionEnded ? this.#policy.resumedSessionEndedMessage() : event.message,
          );
          return;
        }

        // Skip any non-checkpoint event (e.g. retry/progress signalling). This
        // consumer only handles errors (above) and checkpoints (below); new
        // out-of-band event types are ignored by default.
        // eslint-disable-next-line no-continue
        if (!isDuoWorkflowEvent(event)) continue;

        // All other statuses are resumable
        const workflowEnded =
          event.workflowStatus === DuoWorkflowStatus.FINISHED ||
          event.workflowStatus === DuoWorkflowStatus.FAILED;

        if (workflowEnded) {
          this.#workflow = { type: 'dead', id: workflowId };
          // The resume probe produced no output before ending — substitute the
          // friendly notice for the empty terminal checkpoint. A checkpoint that
          // carried output (noCheckpointYet === false) falls through so its
          // content is shown instead.
          if (isResumedSession && noCheckpointYet) {
            yield this.#errorEvent(this.#policy.resumedSessionEndedMessage());
            return;
          }
        } else {
          this.#workflow = { type: 'live', id: workflowId };
        }
        noCheckpointYet = false;
        const availableScopes = this.#getAvailableApprovalScopes();
        const agentEvents = await this.#workflowEventMapper.mapWorkflowEvent(
          event,
          availableScopes,
        );
        for (const agentEvent of agentEvents) {
          yield agentEvent;
        }
      }

      this.#logger.info('Workflow completed successfully');
    } catch (error) {
      this.#logger.error('Workflow failed: ', error);
      let message: string;
      if (isSandboxUnavailableError(error)) {
        // An unavailable sandbox is a local/environment problem, not a dead
        // workflow — surface the actionable message and leave the workflow
        // resumable rather than forcing a fresh conversation.
        message = error.message;
      } else if (isResumedSession) {
        this.#workflow = { type: 'dead', id: workflowId };
        message = this.#policy.resumedSessionEndedMessage();
      } else {
        message = generateErrorMessageFromStatusCode(WorkflowStatusCode.GENERAL_FAILURE);
      }
      yield this.#errorEvent(message);
    } finally {
      // Clean up abort handler
      signal?.removeEventListener('abort', abortHandler);
    }
  }

  /**
   * Explicit flow-config options supplied on a headless `duo run` command line.
   * The inline `flowConfig` is passed through verbatim; the version triplet must
   * be set together or omitted together, and a partial set is discarded (with a
   * warning) so a half-specified flow is never sent. The TUI never registers
   * these flags, so every field is undefined there and this is a harmless no-op.
   */
  #resolveExplicitFlowConfig(): {
    flowConfig: string | undefined;
    flowConfigSchemaVersion: string | undefined;
    flowConfigId: string | undefined;
    flowVersion: string | undefined;
  } {
    const { flowConfig, flowConfigSchemaVersion, flowConfigId, flowVersion } = this.#backendOpts;

    const hasValue = (v: string | undefined): boolean => v !== undefined && v !== '';

    if (hasValue(flowConfigSchemaVersion) && hasValue(flowConfigId) && hasValue(flowVersion)) {
      return { flowConfig, flowConfigSchemaVersion, flowConfigId, flowVersion };
    }

    const entries = Object.entries({ flowVersion, flowConfigId, flowConfigSchemaVersion });
    const provided = entries.filter(([, v]) => hasValue(v)).map(([k]) => k);
    const missing = entries.filter(([, v]) => !hasValue(v)).map(([k]) => k);

    if (provided.length > 0 && missing.length > 0) {
      this.#logger.warn(
        `Flow config options must all be set together or all omitted. Provided: [${provided.join(', ')}], missing: [${missing.join(', ')}]. Discarding all flow config version options.`,
      );
    }

    return {
      flowConfig,
      flowConfigSchemaVersion: undefined,
      flowConfigId: undefined,
      flowVersion: undefined,
    };
  }

  #getAiContextItemsForMessage(action: UserAction, flow: FlowDescriptor): AIContextItem[] {
    // plan_context tells the registry developer flow which mode to render, so it
    // is rebuilt from the active mode and sent on every turn. The legacy
    // chat.Workflow ignores context:inputs.*, so we omit it there.
    const planContextItems: AIContextItem[] = flow.developer
      ? [buildPlanContextItem(this.#activeAgentMode === 'plan')]
      : [];

    // Headless runs include system context on every turn (see the policy).
    if (this.#policy.includeSystemContextEveryTurn) {
      const userContext = this.#policy.initialUserContextItems();
      const aiContextItems = [...this.#systemContext, ...userContext, ...planContextItems];
      this.#logger.debug(
        `AIContextItems: ${this.#systemContext.length} system items + ${userContext.length} user items + ${planContextItems.length} plan_context items = ${aiContextItems.length} total`,
      );
      return aiContextItems;
    }

    // For interactive chat (SendPrompt), include first message context if available
    if (action.type === UserActionType.SendPrompt) {
      if (this.#firstMessageContext) {
        const userContext = action.aiContextItems ?? [];
        const aiContextItems = [...this.#firstMessageContext, ...userContext, ...planContextItems];
        this.#logger.debug(
          `AIContextItems: ${this.#firstMessageContext.length} system items + ${userContext.length} user items + ${planContextItems.length} plan_context items = ${aiContextItems.length} total`,
        );
        this.#firstMessageContext = null; // reset now that it's been included in the workflow once
        return aiContextItems;
      }

      const userContext = action.aiContextItems ?? [];
      const aiContextItems = [...userContext, ...planContextItems];
      this.#logger.debug(
        `AIContextItems: ${userContext.length} user items + ${planContextItems.length} plan_context items = ${aiContextItems.length} total`,
      );
      return aiContextItems;
    }

    // Other action types (e.g. SendToolApproval): send plan_context so the
    // active mode is reconciled on tool-approval turns too (empty on the legacy
    // chat.Workflow path).
    return [...planContextItems];
  }
}
